/**
 * COMPONENTE NEWS LIST
 * ====================
 * Lista de noticias para la sección principal
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../ui/card';
import { Clock } from 'lucide-react';

// URL base para las imágenes de noticias
const PORTADA_BASE_URL = 'https://plazarural.com.uy/uploads/noticias/';

// Formatear fecha relativa (hoy: hace X horas, ayer: ayer, otro: fecha)
const formatRelativeDate = (dateStr) => {
    if (!dateStr) return '';

    const newsDate = new Date(dateStr + 'T12:00:00');
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const newsDay = new Date(newsDate.getFullYear(), newsDate.getMonth(), newsDate.getDate());

    // Si es hoy, mostrar "hace X horas"
    if (newsDay.getTime() === today.getTime()) {
        const hoursAgo = Math.floor((now - newsDate) / (1000 * 60 * 60));
        if (hoursAgo <= 0) return 'Hace un momento';
        if (hoursAgo === 1) return 'Hace 1 hora';
        return `Hace ${hoursAgo} horas`;
    }

    // Si es ayer
    if (newsDay.getTime() === yesterday.getTime()) {
        return 'Ayer';
    }

    // Otro día: mostrar fecha formateada
    return newsDate.toLocaleDateString('es-UY', {
        day: 'numeric',
        month: 'short',
        year: newsDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
};

const NewsList = ({ newsData }) => {
    const navigate = useNavigate();

    // Navegar al detalle de la noticia por slug
    const handleNoticiaClick = (slug) => {
        navigate(`/noticias/${slug}`);
    };

    return (
        <div className="lg:col-span-2">
            <div className="mb-8">
                <h2 className="text-4xl font-bold text-gray-900 mb-2">Mantente <span className="gradient-text">Informado</span></h2>
                <p className="text-gray-600">Las últimas novedades del sector</p>
            </div>

            <div className="space-y-4">
                {newsData.map((news) => {
                    const portadaUrl = news.image
                        ? `${PORTADA_BASE_URL}${news.image}`
                        : null;

                    return (
                        <Card
                            key={news.id}
                            className="overflow-hidden group cursor-pointer border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-auto md:h-[200px]"
                            onClick={() => handleNoticiaClick(news.slug)}
                        >
                            <div className="flex flex-col md:flex-row h-full">
                                <div className="w-full md:w-1/3 relative overflow-hidden h-36 md:h-full flex-shrink-0">
                                    {portadaUrl ? (
                                        <img
                                            src={portadaUrl}
                                            alt={news.title}
                                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                                            <span className="text-2xl font-bold text-green-300">PR</span>
                                        </div>
                                    )}
                                </div>
                                <CardContent className="md:w-2/3 p-5 flex flex-col justify-between">
                                    <div>
                                        <div className="mb-2">
                                            <span className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                                                {news.category}
                                            </span>
                                        </div>
                                        {news.subtitle && (
                                            <p className="text-sm font-medium text-gray-600 mb-2">{news.subtitle}</p>
                                        )}
                                        <h3 className="text-base font-bold text-gray-900 line-clamp-3 group-hover:text-green-600 transition-colors">
                                            {news.title}
                                        </h3>
                                    </div>
                                    <div className="flex items-center text-xs text-gray-500 mt-2">
                                        <Clock className="w-3 h-3 mr-1" />
                                        {formatRelativeDate(news.date)}
                                    </div>
                                </CardContent>
                            </div>
                        </Card>
                    );
                })}
            </div>

            <div className="mt-6">
                <button
                    onClick={() => navigate('/noticias')}
                    className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                    Ver más noticias
                </button>
            </div>
        </div>
    );
};

export default NewsList;
